import React from 'react';
import {useLoaderData} from 'react-router-dom';
import {Col, Row} from "react-bootstrap";
import FacilityInfo from "./FacilityInfo";
import DetailsPageHeader from "../../components/common/headers/DetailsPageHeader";
import graphql from "babel-plugin-relay/macro";
import {usePreloadedQuery} from "react-relay";
import {useUser} from "../../hooks/useUser";
import DeletePanel from "../../components/common/repeated-page-panels/DeletePanel";
import FacilityVersions from "./versions/FacilityVersions";
import FacilityContactPoints from "./contactPoints/FacilityContactPoints";
import FacilityIdentifiers from "./identifiers/FacilityIdentifiers";
import FacilityLocations from "./locations/FacilityLocations";

const FacilityDetails = () => {
    const loader = useLoaderData();
    const data = usePreloadedQuery(graphql`
    query FacilityDetailsQuery($id: ID!) {
        node(id: $id) {
            id
            ... on Facility {
                ...FacilityInfo_Facility
                applicableVersion {
                    displayName
                }
                legalContract {
                    id
                    name
                    locations {
                        ...LocationFragments_LocationDropdown
                    }
                    linesOfService {
                        ...ContractLinesOfServiceFragments_LinesOfService
                    }
                    billingGroups {
                        ...BillingGroupFragments_BillingGroupDropdown
                    }
                }
                versions {
                    ...FacilityVersions_Versions
                }
                contactPoints {
                    ...FacilityContactPoints_ContactPoints
                }
                identifiers {
                    ...FacilityIdentifiers_Identifiers
                }
                locations {
                    ...FacilityLocations_Locations
                }
            }
        }
        allIdentifierUseCodes {
            ...Fragments_AllIdentifierUseCodes
        }
        allIdentifierTypes {
            ...Fragments_AllIdentifierTypes
        }
        allLegalContracts {
            ...ContractFragments_AllContractsDropdown
        }
        allContactPointUseCodes {
            ...Fragments_AllContactPointUseCodesDropdown
        }
        allContactPointSystemCodes {
            ...Fragments_AllContactPointSystemCodesDropdown
        }
    }`, loader.facilityDetailsQuery);
    const deleteMutation = graphql`
    mutation FacilityDetails_DeleteMutation($id: ID!) {
        deleteNode(input: {id: $id}) {
            deletedNodeId @deleteRecord
        }
    }`
    const {hasPermission} = useUser();
    return (
        <>
            <DetailsPageHeader iconText={"Facility"} iconType={"building"} title={data.node.applicableVersion.displayName} />
            <Row className={"mb-3"}>
                <FacilityInfo facility={data.node} legalContracts={data.allLegalContracts}/>
            </Row>
            <Row>
                <Col>
                    <FacilityVersions facilityId={data.node.id}
                                          versions={data.node.versions}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    {hasPermission("FacilityIdentifier.Read") &&  <FacilityIdentifiers facilityId={data.node.id}
                                                                                            identifiers={data.node.identifiers}
                                                                                            allIdentifierTypes={data.allIdentifierTypes}
                                                                                            allIdentifierUseCodes={data.allIdentifierUseCodes}
                    />}
                </Col>
                <Col>
                    {hasPermission("FacilityContactPoint.Read") && <FacilityContactPoints facilityId={data.node.id}
                                                                                           contactPoints={data.node.contactPoints}
                                                                                           systemCodes={data.allContactPointSystemCodes}
                                                                                           useCodes={data.allContactPointUseCodes}
                    />}
                </Col>
            </Row>
            <Row>
                <Col>
                    {hasPermission("FacilityLocation.Read") && <FacilityLocations facilityId={data.node.id}
                                                                                       allLocations={data.node.legalContract.locations}
                                                                                       allBillingGroups={data.node.legalContract.billingGroups}
                                                                                       locations={data.node.locations}
                                                                                       availableLineOfServices={data.node.legalContract.linesOfService}
                    />}
                </Col>
            </Row>
            {hasPermission("Facility.Delete") &&
                <DeletePanel mutation={deleteMutation}
                             redirectUrl={"/facilities"}
                             thingToDelete={"Facility"}
                             id={data.node.id}>
                    In order to delete a facility, there must be only 1 version and no other related data.
                </DeletePanel>}
        </>
    );
};

export default FacilityDetails;
